// Accessibility utilities for WCAG AA compliance

// Focus management
export class FocusManager {
  private static previousFocus: HTMLElement | null = null
  private static trapElements: HTMLElement[] = []

  // Save current focus and move to target
  static saveFocusAndMoveTo(target: HTMLElement | string) {
    this.previousFocus = document.activeElement as HTMLElement
    const element = typeof target === 'string' ? document.querySelector(target) as HTMLElement : target
    if (element) {
      element.focus()
    }
  }

  // Restore previously saved focus
  static restoreFocus() {
    if (this.previousFocus && this.previousFocus.isConnected) {
      this.previousFocus.focus()
    }
    this.previousFocus = null
  }

  // Focus trap for modals
  static trapFocus(container: HTMLElement) {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ) as NodeListOf<HTMLElement>

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement.focus()
        }
      }
    }

    container.addEventListener('keydown', handleTabKey)
    this.trapElements.push(container)

    // Focus first element
    if (firstElement) {
      firstElement.focus()
    }

    return () => {
      container.removeEventListener('keydown', handleTabKey)
      this.trapElements = this.trapElements.filter(el => el !== container)
    }
  }
}

// ARIA live region announcements
export class ScreenReaderAnnouncer {
  private static liveRegion: HTMLElement | null = null

  static init() {
    if (!this.liveRegion) {
      this.liveRegion = document.createElement('div')
      this.liveRegion.setAttribute('aria-live', 'polite')
      this.liveRegion.setAttribute('aria-atomic', 'true')
      this.liveRegion.className = 'sr-only'
      this.liveRegion.style.cssText = `
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
      `
      document.body.appendChild(this.liveRegion)
    }
  }

  static announce(message: string, priority: 'polite' | 'assertive' = 'polite') {
    if (!this.liveRegion) this.init()
    
    this.liveRegion!.setAttribute('aria-live', priority)
    this.liveRegion!.textContent = message

    // Clear after announcement
    setTimeout(() => {
      this.liveRegion!.textContent = ''
    }, 1000)
  }
}

// Keyboard navigation helpers
export const KeyboardNavigation = {
  // Handle escape key
  onEscape: (callback: () => void) => (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      callback()
    }
  },

  // Handle enter/space activation
  onActivate: (callback: () => void) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      callback()
    }
  },

  // Arrow key navigation for lists/grids
  arrowNavigation: (container: HTMLElement, itemSelector: string) => {
    const items = Array.from(container.querySelectorAll(itemSelector)) as HTMLElement[]
    
    return (e: KeyboardEvent) => {
      const currentIndex = items.indexOf(document.activeElement as HTMLElement)
      let nextIndex = -1

      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
          break
        case 'ArrowUp':
        case 'ArrowLeft':
          nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
          break
        case 'Home':
          nextIndex = 0
          break
        case 'End':
          nextIndex = items.length - 1
          break
        default:
          return
      }

      if (nextIndex !== -1) {
        e.preventDefault()
        items[nextIndex].focus()
      }
    }
  }
}

// Color contrast utilities
export const ColorContrast = {
  // Calculate contrast ratio
  getContrastRatio: (color1: string, color2: string): number => {
    const getLuminance = (color: string): number => {
      // Convert hex to RGB
      const hex = color.replace('#', '')
      const r = parseInt(hex.substr(0, 2), 16) / 255
      const g = parseInt(hex.substr(2, 2), 16) / 255
      const b = parseInt(hex.substr(4, 2), 16) / 255

      // Calculate relative luminance
      const getChannel = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
      return 0.2126 * getChannel(r) + 0.7152 * getChannel(g) + 0.0722 * getChannel(b)
    }

    const lum1 = getLuminance(color1)
    const lum2 = getLuminance(color2)
    const brightest = Math.max(lum1, lum2)
    const darkest = Math.min(lum1, lum2)
    
    return (brightest + 0.05) / (darkest + 0.05)
  },

  // Check WCAG compliance
  isWCAGCompliant: (foreground: string, background: string, level: 'AA' | 'AAA' = 'AA'): boolean => {
    const ratio = ColorContrast.getContrastRatio(foreground, background)
    return level === 'AA' ? ratio >= 4.5 : ratio >= 7
  }
}

// Motion preferences
export const MotionPreferences = {
  // Check if user prefers reduced motion
  prefersReducedMotion: (): boolean => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  },

  // Get animation duration based on preference
  getAnimationDuration: (normalDuration: number): number => {
    return MotionPreferences.prefersReducedMotion() ? 0 : normalDuration
  },

  // Conditional animation class
  getAnimationClass: (animationClass: string): string => {
    return MotionPreferences.prefersReducedMotion() ? '' : animationClass
  }
}

// Skip links utility
export const SkipLinks = {
  create: (targets: Array<{ label: string; target: string }>) => {
    const skipContainer = document.createElement('div')
    skipContainer.className = 'skip-links'
    skipContainer.style.cssText = `
      position: absolute;
      top: -40px;
      left: 6px;
      background: #000;
      color: #fff;
      padding: 8px;
      z-index: 9999;
      text-decoration: none;
      border-radius: 4px;
      font-weight: bold;
    `

    targets.forEach(({ label, target }) => {
      const link = document.createElement('a')
      link.href = `#${target}`
      link.textContent = label
      link.style.cssText = `
        color: #fff;
        text-decoration: none;
        margin-right: 10px;
      `
      
      link.addEventListener('focus', () => {
        skipContainer.style.top = '6px'
      })
      
      link.addEventListener('blur', () => {
        skipContainer.style.top = '-40px'
      })
      
      link.addEventListener('click', (e) => {
        e.preventDefault()
        const targetElement = document.querySelector(target)
        if (targetElement) {
          (targetElement as HTMLElement).focus()
          targetElement.scrollIntoView({ behavior: 'smooth' })
        }
      })

      skipContainer.appendChild(link)
    })

    document.body.insertBefore(skipContainer, document.body.firstChild)
  }
}

// Form accessibility helpers
export const FormAccessibility = {
  // Associate labels with inputs
  associateLabel: (input: HTMLElement, labelText: string) => {
    const id = input.id || `input-${Math.random().toString(36).substr(2, 9)}`
    input.id = id

    const existingLabel = document.querySelector(`label[for="${id}"]`)
    if (!existingLabel) {
      const label = document.createElement('label')
      label.htmlFor = id
      label.textContent = labelText
      input.parentNode?.insertBefore(label, input)
    }
  },

  // Add error messaging
  addErrorMessage: (input: HTMLElement, message: string) => {
    const errorId = `${input.id}-error`
    let errorElement = document.getElementById(errorId)

    if (!errorElement) {
      errorElement = document.createElement('div')
      errorElement.id = errorId
      errorElement.setAttribute('role', 'alert')
      errorElement.setAttribute('aria-live', 'polite')
      errorElement.className = 'error-message'
      input.parentNode?.appendChild(errorElement)
    }

    errorElement.textContent = message
    input.setAttribute('aria-describedby', errorId)
    input.setAttribute('aria-invalid', 'true')
  },

  // Clear error messaging
  clearErrorMessage: (input: HTMLElement) => {
    const errorId = `${input.id}-error`
    const errorElement = document.getElementById(errorId)
    
    if (errorElement) {
      errorElement.textContent = ''
    }
    
    input.removeAttribute('aria-describedby')
    input.removeAttribute('aria-invalid')
  }
}

// Initialize accessibility features
export const initAccessibility = () => {
  // Initialize screen reader announcer
  ScreenReaderAnnouncer.init()

  // Skip links are handled in App.tsx, no need to create duplicates here
  // SkipLinks.create([
  //   { label: 'Ir al contenido principal', target: '#main-content' },
  //   { label: 'Ir a la navegación', target: '#navigation' },
  //   { label: 'Ir a los sorteos', target: '#raffles-section' }
  // ])

  // Monitor focus visibility
  let hadKeyboardEvent = true
  const keyboardThrottleTimeout = 100

  const detectKeyboard = (e: KeyboardEvent) => {
    if (e.metaKey || e.altKey || e.ctrlKey) return
    hadKeyboardEvent = true
  }

  const detectMouse = () => {
    hadKeyboardEvent = false
    setTimeout(() => hadKeyboardEvent = true, keyboardThrottleTimeout)
  }

  document.addEventListener('keydown', detectKeyboard, true)
  document.addEventListener('mousedown', detectMouse, true)

  // Add focus-visible polyfill behavior
  const addFocusVisibleClass = () => {
    if (hadKeyboardEvent) {
      document.body.classList.add('keyboard-focused')
    } else {
      document.body.classList.remove('keyboard-focused')
    }
  }

  document.addEventListener('focusin', addFocusVisibleClass)
  document.addEventListener('focusout', addFocusVisibleClass)
}

export default {
  FocusManager,
  ScreenReaderAnnouncer,
  KeyboardNavigation,
  ColorContrast,
  MotionPreferences,
  SkipLinks,
  FormAccessibility,
  initAccessibility
}